import { useState } from "react";
import { comparisonItems } from "../../../mocks/technology";

export default function ComparisonSection() {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  return (
    <section id="comparison" className="py-14 md:py-20 bg-cream">
      <div className="container-main px-4 md:px-8 lg:px-12">
        <div className="text-center mb-10 md:mb-14">
          <span className="tag-label mb-3 inline-block">DETAILED COMPARISON</span>
          <h2 className="mb-3">10 大維度全方位比較</h2>
          <p className="text-medium-gray max-w-2xl mx-auto">
            從業主最關心的實際面向切入，客觀呈現傳統 RC 工法與預製鋼構工法的差異。
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block">
          <div className="bg-white rounded-2xl border border-warm-gray/20 overflow-hidden shadow-sm">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_1.2fr_1.2fr] bg-graphite text-cream">
              <div className="px-6 py-4 font-semibold text-base">比較項目</div>
              <div className="px-6 py-4 font-semibold text-base text-center border-l border-white/10">
                傳統 RC 工法
              </div>
              <div className="px-6 py-4 font-semibold text-base text-center border-l border-white/10 bg-champagne/20 text-champagne">
                <span className="w-5 h-5 inline-flex items-center justify-center mr-1">
                  <i className="ri-star-line" />
                </span>
                預製鋼構工法
              </div>
            </div>

            {/* Table Rows */}
            {comparisonItems.map((item, idx) => (
              <div
                key={item.dimension}
                className={`grid grid-cols-[1fr_1.2fr_1.2fr] transition-colors duration-200 ${
                  hoveredIdx === idx ? "bg-cream/60" : idx % 2 === 0 ? "bg-white" : "bg-cream/30"
                }`}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <div className="px-6 py-4 flex items-center gap-3 border-t border-warm-gray/10">
                  <span className="w-8 h-8 rounded-lg bg-champagne/10 flex items-center justify-center text-champagne shrink-0">
                    <i className={`${item.icon} text-sm`} />
                  </span>
                  <span className="font-semibold text-graphite text-base">{item.dimension}</span>
                </div>
                <div className="px-6 py-4 border-l border-t border-warm-gray/10 text-center">
                  <p className="font-semibold text-medium-gray text-base">{item.traditional}</p>
                  <p className="text-sm text-medium-gray/70 mt-1 leading-snug">{item.traditionalDesc}</p>
                </div>
                <div className="px-6 py-4 border-l border-t border-warm-gray/10 text-center bg-champagne/5">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <p className="font-semibold text-graphite text-base">{item.prefab}</p>
                    {item.winner === "prefab" && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                        優勢
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-medium-gray mt-1 leading-snug">{item.prefabDesc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4">
          {comparisonItems.map((item, idx) => (
            <div
              key={item.dimension}
              className={`bg-white rounded-xl border transition-all duration-300 ${
                hoveredIdx === idx ? "border-champagne/40 shadow-md" : "border-warm-gray/20"
              }`}
              onClick={() => setHoveredIdx(hoveredIdx === idx ? null : idx)}
            >
              <div className="px-5 py-4 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-champagne/10 flex items-center justify-center text-champagne shrink-0">
                  <i className={`${item.icon} text-sm`} />
                </span>
                <span className="font-semibold text-graphite text-base">{item.dimension}</span>
                <span className="ml-auto w-7 h-7 rounded-full bg-champagne/10 flex items-center justify-center">
                  <i className={`ri-${hoveredIdx === idx ? "subtract" : "add"}-line text-champagne text-sm`} />
                </span>
              </div>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  hoveredIdx === idx ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="px-5 pb-5 space-y-4">
                  <div className="p-3 rounded-lg bg-cream/50 border border-warm-gray/10">
                    <p className="text-sm font-semibold text-medium-gray mb-1">傳統 RC</p>
                    <p className="text-base font-semibold text-graphite">{item.traditional}</p>
                    <p className="text-sm text-medium-gray/70 mt-1">{item.traditionalDesc}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-champagne/5 border border-champagne/20">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-champagne">預製鋼構</p>
                      {item.winner === "prefab" && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                          優勢
                        </span>
                      )}
                    </div>
                    <p className="text-base font-semibold text-graphite">{item.prefab}</p>
                    <p className="text-sm text-medium-gray mt-1">{item.prefabDesc}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
          <span className="inline-flex items-center gap-1.5 text-sm text-medium-gray">
            <span className="w-3 h-3 rounded-full bg-emerald-100 border border-emerald-200" />
            預製鋼構優勢項目
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm text-medium-gray">
            <span className="w-3 h-3 rounded-full bg-warm-gray/40 border border-warm-gray/30" />
            各有所長 / 依需求選擇
          </span>
        </div>
      </div>
    </section>
  );
}